import React, { useState, useEffect } from 'react';
import { auth, db, functions, clearFirebaseStorageState, isAppCheckThrottleError } from '../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  updateProfile
} from 'firebase/auth';
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import PopupMessage from '../components/PopupMessage';
import EmailVerificationPendingModal from '../components/EmailVerificationPendingModal';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const inputStyle = {
  padding: '12px',
  paddingRight: '60px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  lineHeight: '1.5',
  boxSizing: 'border-box',
  width: '100%'
};

const Login = () => {
  const [isNew, setIsNew] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('info');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const navigate = useNavigate();
  const { user: currentUser, loading: authLoading } = useAuth();

  // Auto-redirect if user is already logged in AND verified
  // Don't redirect if email is not verified - let them see the verification modal
  useEffect(() => {
    if (!authLoading && currentUser) {
      // Check if email is verified before redirecting
      if (currentUser.emailVerified) {
        // Always redirect to /home when already logged in and verified
        console.log('✅ User already logged in and verified, redirecting to /home');
        navigate('/home', { replace: true });
      } else {
        // User is logged in but not verified - show verification modal
        console.log('⚠️ User logged in but email not verified, showing verification modal');
        setVerificationEmail(currentUser.email || '');
        setShowVerificationModal(true);
      }
    }
  }, [currentUser, authLoading, navigate]);

  const setPopupMessageWithTimeout = (message, type = 'info') => {
    setPopupMessage(message);
    setPopupType(type);
    setTimeout(() => {
      setPopupMessage('');
    }, 5000);
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();

    try {
      // Set session persistence based on "Stay logged in" checkbox
      await setPersistence(auth, stayLoggedIn ? browserLocalPersistence : browserSessionPersistence);

      let userCred;

      if (isNew) {
        // Validate email and password
        if (!/\S+@\S+\.\S+/.test(cleanEmail)) {
          setPopupMessageWithTimeout("Please enter a valid email address.", 'error');
          return;
        }

        // Validate password requirements: 8+ chars and at least 1 capital letter
        if (password.length < 8) {
          setPopupMessageWithTimeout("Password must be at least 8 characters long.", 'error');
          return;
        }

        if (!/[A-Z]/.test(password)) {
          setPopupMessageWithTimeout("Password must contain at least one capital letter.", 'error');
          return;
        }

        if (password !== confirm) {
          setPopupMessageWithTimeout("Passwords do not match.", 'error');
          return;
        }

        // Validate company code via Cloud Function (secure, no direct Firestore read)
        const validateCompanyCode = httpsCallable(functions, 'validateCompanyCode');
        const { data: companyResult } = await validateCompanyCode({ companyCode: companyCode.trim() });

        if (!companyResult?.success || !companyResult?.companyId) {
          setPopupMessageWithTimeout(companyResult?.message || "Invalid company code.", 'error');
          return;
        }

        const {
          companyId,
          contactEmail: subscriptionContactEmail,
          maxUsers,
        } = companyResult;

        // Soft hint if signup email differs from subscription contact email
        if (subscriptionContactEmail && subscriptionContactEmail.toLowerCase() !== cleanEmail) {
          setPopupMessageWithTimeout(
            `Note: the subscription contact email is ${subscriptionContactEmail}. You can still continue with a different admin email.`,
            'info'
          );
        }

        // Seat limit check before creating the user (UX guard; hard gate is in rules).
        // Subscription contact (admin) always has a reserved seat and is not blocked when at capacity.
        if (maxUsers && companyId) {
          try {
            const usersRef = collection(db, "users");
            const pmQuery = query(
              usersRef,
              where("companyId", "==", companyId),
              where("role", "==", "pm")
            );
            const snapshot = await getDocs(pmQuery);
            const pmCount = snapshot.size;
            const isSubscriptionContact = subscriptionContactEmail && (subscriptionContactEmail.toLowerCase() === cleanEmail);
            if (pmCount >= maxUsers && !isSubscriptionContact) {
              setPopupMessageWithTimeout(
                "You've reached your seat limit for this subscription. Remove a user or upgrade your plan to add more.",
                'error'
              );
              return;
            }
          } catch (seatErr) {
            console.error("Failed to check seat count:", seatErr);
            // Fail-open for UX; Firestore rules still enforce the hard limit
          }
        }

        // Create the user in Firebase Auth
        userCred = await createUserWithEmailAndPassword(auth, cleanEmail, password);

        // Set display name to the first name only
        await updateProfile(userCred.user, {
          displayName: firstName.trim()
        });

        // Store user info in Firestore immediately
        await setDoc(doc(db, "users", userCred.user.uid), {
          email: cleanEmail,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          role: "pm",
          companyId,
          createdAt: serverTimestamp()
        });

        // Send email verification (custom-branded, via Cloud Function — see AuthAction.jsx)
        const sendVerification = httpsCallable(functions, 'sendEmailVerificationCustom');
        await sendVerification();

        // Show verification modal instead of just a popup message
        setVerificationEmail(cleanEmail);
        setShowVerificationModal(true);
        return; // Stop here so they verify before logging in
      } else {
        // Existing user logs in
        userCred = await signInWithEmailAndPassword(auth, cleanEmail, password);

        // Check if the user exists in Firestore FIRST
        const userDocRef = doc(db, "users", userCred.user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) {
          // Orphaned Auth account - Auth exists but Firestore doc was deleted
          // This can happen after account deletion if Auth deletion failed
          // Require email verification again for security
          if (!userCred.user.emailVerified) {
            // Send verification email (custom-branded, via Cloud Function)
            const sendVerification = httpsCallable(functions, 'sendEmailVerificationCustom');
            await sendVerification();
            // Show verification modal
            setVerificationEmail(cleanEmail);
            setShowVerificationModal(true);
            return;
          }

          // If verified but no Firestore doc, create it (validate company code via function)
          const validateCompanyCode = httpsCallable(functions, 'validateCompanyCode');
          const { data: companyResult } = await validateCompanyCode({ companyCode: companyCode.trim() });

          if (!companyResult?.success || !companyResult?.companyId) {
            setPopupMessageWithTimeout(companyResult?.message || "Company code invalid. Try again or contact support.", 'error');
            return;
          }

          const {
            companyId,
            contactEmail: subscriptionContactEmail,
            maxUsers,
          } = companyResult;

          // Optional hint if email differs from subscription contact
          if (subscriptionContactEmail && subscriptionContactEmail.toLowerCase() !== cleanEmail) {
            setPopupMessageWithTimeout(
              `Note: the subscription contact email is ${subscriptionContactEmail}. You can still continue with a different admin email.`,
              'info'
            );
          }

          // Seat limit check before recreating PM profile (contact always has reserved seat)
          if (maxUsers && companyId) {
            try {
              const usersRef = collection(db, "users");
              const pmQuery = query(
                usersRef,
                where("companyId", "==", companyId),
                where("role", "==", "pm")
              );
              const snapshot = await getDocs(pmQuery);
              const pmCount = snapshot.size;
              const isSubscriptionContact = subscriptionContactEmail && (subscriptionContactEmail.toLowerCase() === cleanEmail);
              if (pmCount >= maxUsers && !isSubscriptionContact) {
                setPopupMessageWithTimeout(
                  "You've reached your seat limit for this subscription. Remove a user or upgrade your plan to add more.",
                  'error'
                );
                return;
              }
            } catch (seatErr) {
              console.error("Failed to check seat count for orphaned account:", seatErr);
            }
          }

          // Create user document and wait for it to complete
          await setDoc(userDocRef, {
            email: cleanEmail,
            firstName: userCred.user.displayName || "", // fallback
            lastName: "", // could update later via settings
            role: "pm",
            companyId,
            createdAt: serverTimestamp()
          });

          // Verify the document was created before proceeding
          const verifyDocSnap = await getDoc(userDocRef);
          if (!verifyDocSnap.exists()) {
            setPopupMessageWithTimeout("Failed to create user profile. Please try again.", 'error');
            return;
          }

          console.log('✅ User document created for orphaned Auth account');
        } else {
          // User document exists - check email verification normally
          if (!userCred.user.emailVerified) {
            // Send verification email if not already sent recently (custom-branded, via Cloud Function)
            try {
              const sendVerification = httpsCallable(functions, 'sendEmailVerificationCustom');
              await sendVerification();
            } catch (err) {
              // Ignore errors (might already be sent recently)
              console.log('Verification email send result:', err.code);
            }
            // Show verification modal
            setVerificationEmail(cleanEmail);
            setShowVerificationModal(true);
            return;
          }
        }
      }

      // ALWAYS redirect to /home after login to ensure fresh start
      // This prevents any cross-account redirects from browser history or location state
      console.log('✅ Login successful, redirecting to /home (ignoring any saved location)');
      // Use navigate instead of window.location.href to avoid full page reload
      // This allows React Router to handle the route properly and prevents syntax errors
      navigate('/home', { replace: true });
    } catch (err) {
      console.error(err);

      if (isAppCheckThrottleError(err)) {
        setPopupMessageWithTimeout("Refreshing security check, please wait…", 'error');
        clearFirebaseStorageState();
        setTimeout(() => window.location.reload(), 1500);
        return;
      }

      // Handle specific Firebase errors
      if (err.code === "auth/email-already-in-use") {
        setPopupMessageWithTimeout("An account with this email already exists. Please sign in instead, or contact support if you believe this is an error.", 'error');
      } else if (err.code === "auth/invalid-email") {
        setPopupMessageWithTimeout("Invalid email address.", 'error');
      } else if (err.code === "auth/weak-password") {
        setPopupMessageWithTimeout("Password is too weak. Must be at least 8 characters with one capital letter.", 'error');
      } else if (err.code === "auth/wrong-password") {
        setPopupMessageWithTimeout("Incorrect password. Please try again.", 'error');
      } else if (err.code === "auth/user-not-found") {
        setPopupMessageWithTimeout("User does not exist. Please sign up.", 'error');
      } else if (err.code === "auth/invalid-credential") {
        setPopupMessageWithTimeout("Incorrect password or user does not exist.", 'error');
      } else {
        setPopupMessageWithTimeout(err.message, 'error');
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const cleanResetEmail = resetEmail.trim().toLowerCase();

    try {
      // Validate email format
      if (!/\S+@\S+\.\S+/.test(cleanResetEmail)) {
        setPopupMessageWithTimeout("Please enter a valid email address.", 'error');
        return;
      }

      // Custom-branded reset email via Cloud Function (see AuthAction.jsx / authEmails.js).
      // The function itself never reveals whether the account exists — it always
      // resolves with the same generic message, same protection as before.
      const sendReset = httpsCallable(functions, 'sendPasswordResetEmailCustom');
      const { data } = await sendReset({ email: cleanResetEmail });
      setPopupMessageWithTimeout(data?.message || "If an account with that email exists, a password reset link has been sent. Please check your inbox.", 'success');
      setIsForgotPassword(false);
      setResetEmail('');
    } catch (err) {
      console.error(err);

      if (isAppCheckThrottleError(err)) {
        setPopupMessageWithTimeout("Refreshing security check, please wait…", 'error');
        clearFirebaseStorageState();
        setTimeout(() => window.location.reload(), 1500);
        return;
      }

      setPopupMessageWithTimeout(err.message || "Could not send the email. Please try again.", 'error');
    }
  };

  const year = new Date().getFullYear();

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: '#f7f9fc',
      fontFamily: 'sans-serif',
      position: 'relative'
    }}>
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px',
      }}>
      <div style={{
        background: '#f7f9fc',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 4px 30px rgba(0,0,0,0.05)',
        border: '1px solid var(--branding-app-primary)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        width: '100%',
        maxWidth: '400px',
        position: 'relative'
      }}>
        <img src="/CrewChain%20Transparent.png" alt="Crewchain Logo" style={{ width: '250px', marginBottom: '20px' }} />

        {popupMessage && (
          <PopupMessage
            message={popupMessage}
            type={popupType}
            onClose={() => setPopupMessage('')}
          />
        )}

        {!isForgotPassword ? (
          <form onSubmit={handleAuth} style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>
              {isNew ? 'Sign up' : 'Sign in'}
            </h2>

            {isNew && (
              <>
                <input type="text" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required style={inputStyle} />
                <input type="text" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} required style={inputStyle} />
              </>
            )}

            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />

            <div style={{ position: 'relative', width: '100%' }}>
              <input type={showPassword ? 'text' : 'password'} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />
              {isNew && (
                <div style={{ position: 'absolute', top: '50%', right: '70px', transform: 'translateY(-50%)' }}>
                  <div 
                    onMouseEnter={() => setShowPasswordTooltip(true)}
                    onMouseLeave={() => setShowPasswordTooltip(false)}
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: '2px solid var(--branding-app-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: 'var(--branding-app-primary)',
                      cursor: 'help',
                      userSelect: 'none'
                    }}
                  >
                    ?
                  </div>
                  {showPasswordTooltip && (
                    <div style={{
                      position: 'absolute',
                      bottom: '30px',
                      right: '-10px',
                      backgroundColor: 'var(--branding-app-primary)',
                      color: 'var(--branding-app-primary-fg)',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      zIndex: 1000
                    }}>
                      Password must be at least 8 characters<br/>and contain at least one capital letter
                      <div style={{
                        position: 'absolute',
                        bottom: '-6px',
                        right: '15px',
                        width: '0',
                        height: '0',
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: '6px solid var(--branding-app-primary)'
                      }}></div>
                    </div>
                  )}
                </div>
              )}
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--branding-app-primary)', fontSize: '14px', fontWeight: '500'
              }}>
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>

            {isNew && (
              <>
                <input type="password" placeholder="Confirm Password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required style={inputStyle} />
                <input type="text" placeholder="Company Code" value={companyCode} onChange={(e) => setCompanyCode(e.target.value)} required style={inputStyle} />
                
                <p style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', margin: '8px 0' }}>
                  By signing up, you agree to our{' '}
                  <a href="https://crewchain.co.uk/terms/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--branding-app-primary)', textDecoration: 'underline' }}>
                    Terms of Service
                  </a>
                  ,{' '}
                  <a href="https://crewchain.co.uk/privacy/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--branding-app-primary)', textDecoration: 'underline' }}>
                    Privacy Policy
                  </a>
                  , and{' '}
                  <a href="https://crewchain.co.uk/eula/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--branding-app-primary)', textDecoration: 'underline' }}>
                    EULA
                  </a>
                  .
                </p>
              </>
            )}

            {!isNew && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" id="stayLoggedIn" checked={stayLoggedIn} onChange={(e) => setStayLoggedIn(e.target.checked)} />
                <label htmlFor="stayLoggedIn" style={{ fontSize: '14px', color: '#1e293b' }}>
                  Stay signed in
                </label>
              </div>
            )}

            <button type="submit" style={{
              padding: '12px', backgroundColor: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontWeight: '600', fontSize: '16px'
            }}>
              {isNew ? 'Sign up' : 'Sign in'}
            </button>

            {!isForgotPassword && !isNew && (
              <p onClick={() => {
                setIsForgotPassword(true);
                setIsNew(false);
              }} style={{
                cursor: 'pointer', color: 'var(--branding-app-primary)', textAlign: 'center',
                fontSize: '14px'
              }}>
                Forgot password?
              </p>
            )}

            <p onClick={() => {
              setIsNew(!isNew);
              setIsForgotPassword(false);
            }} style={{
              cursor: 'pointer', color: 'var(--branding-app-primary)', textAlign: 'center',
              marginTop: '8px', fontSize: '14px'
            }}>
              {isNew ? 'Already have an account? Sign in' : 'New user? Sign up'}
            </p>

            {isNew && (
              <>
                <p style={{ textAlign: 'center', margin: '4px 0 0', fontSize: '14px', color: '#64748b' }}>
                  No company code?{' '}
                  <a
                    href="https://crewchain.co.uk/pricing/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--branding-app-primary)', fontWeight: 500, textDecoration: 'underline' }}
                  >
                    Create your company account
                  </a>
                </p>
              </>
            )}
          </form>
        ) : (
          <form onSubmit={handleForgotPassword} style={{
            display: 'flex', flexDirection: 'column',
            gap: '16px', width: '100%'
          }}>
            <h2 style={{
              fontSize: '24px', fontWeight: 'bold',
              color: '#1e293b', marginBottom: '8px'
            }}>Reset password</h2>
            <input type="email" placeholder="Enter your email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required style={inputStyle} />
            <button type="submit" style={{
              padding: '12px', backgroundColor: 'var(--branding-app-primary)',
              color: 'var(--branding-app-primary-fg)', border: 'none', borderRadius: '8px',
              cursor: 'pointer', fontWeight: '600', fontSize: '16px'
            }}>Send reset link</button>
            <p onClick={() => {
              setIsForgotPassword(false);
              setResetEmail('');
            }} style={{
              cursor: 'pointer', color: 'var(--branding-app-primary)', textAlign: 'center',
              fontSize: '14px', marginTop: '8px'
            }}>
              Back to sign in
            </p>
          </form>
        )}
      </div>
      </div>

      <footer
        style={{
          textAlign: 'center',
          padding: '12px 24px 24px',
          fontSize: '12px',
          color: '#64748b',
          flexShrink: 0,
        }}
      >
        © {year} Crew Chain Ltd.
      </footer>

      {/* Email Verification Modal */}
      <EmailVerificationPendingModal
        open={showVerificationModal}
        email={verificationEmail}
        onClose={() => {
          setShowVerificationModal(false);
          setVerificationEmail('');
        }}
        onVerified={async () => {
          // Reload user to get updated verification status
          const user = auth.currentUser;
          if (user) {
            await user.reload();
            if (user.emailVerified) {
              setShowVerificationModal(false);
              setVerificationEmail('');
              // Wait a moment for AuthContext to update after reload
              // This prevents race conditions where ProtectedRoute checks before AuthContext updates
              await new Promise(resolve => setTimeout(resolve, 500));
              // Use navigate instead of window.location.href to avoid full page reload
              // This allows React Router to handle the route properly
              navigate('/home', { replace: true });
            }
          }
        }}
      />
    </div>
  );
};

export default Login;
